import * as React from 'react';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { StatusTooltip } from './StatusTooltip';
import { ProcessingTooltip, ProcessingTooltipProps } from './ProcessingTooltip';

/**
 * The statuses a row can call out. `isEnabled` decides which ones it does:
 * `completed` is off because done is what most of the tree is, and a tick on
 * every row was noise that hid the rows still processing or in trouble.
 */
const STATUS_CONFIG: Record<
  string,
  {
    /** Whether the row shows this status at all. */
    isEnabled: boolean;
    label: string;
    Icon: React.ElementType;
    iconProps?: Record<string, unknown>;
    color: (theme: Theme) => string;
  }
> = {
  processing: {
    isEnabled: true,
    label: 'Processing',
    Icon: CircularProgress,
    iconProps: { size: 16 },
    color: (theme) => theme.palette.text.secondary,
  },
  warning: {
    isEnabled: true,
    label: 'Warning',
    Icon: WarningAmberIcon,
    color: (theme) => theme.palette.warning.main,
  },
  failed: {
    isEnabled: true,
    label: 'Error',
    Icon: ErrorIcon,
    color: (theme) => theme.palette.error.main,
  },
  completed: {
    isEnabled: false,
    label: 'Completed',
    Icon: CheckCircleIcon,
    color: (theme) => theme.palette.success.main,
  },
};

// Hoisted so the style objects aren't rebuilt for every row of the tree.
// Flex, so the icon isn't set on a line of text: the line's descender space
// made the box taller than the name beside it and nudged the row's name up.
const WRAPPER_SX = { display: 'flex', marginLeft: 'auto' } as const;

const ICON_SX_CACHE = new Map<string, Record<string, unknown>>();

const getIconSx = (status: string, color: (theme: Theme) => string) => {
  const cached = ICON_SX_CACHE.get(status);
  if (cached) return cached;
  const sx = { color, marginLeft: '6px' };
  ICON_SX_CACHE.set(status, sx);
  return sx;
};

export type FileStatusProps = {
  status: string;
  /** Read only while `status` is `processing`: what its tooltip says. */
  processing?: ProcessingTooltipProps;
};

export const FileStatus = React.memo(
  ({ status, processing }: FileStatusProps) => {
    const config = STATUS_CONFIG[status];
    if (!config?.isEnabled) return null;

    const { Icon, iconProps = {} } = config;
    // A component rather than a string: the tooltip mounts it only when it opens,
    // so the elapsed time is measured at hover rather than at the last tree render.
    const title =
      status === 'processing' ? (
        <ProcessingTooltip {...processing} />
      ) : (
        config.label
      );

    return (
      <Box sx={WRAPPER_SX}>
        <StatusTooltip title={title}>
          <Icon
            fontSize="small"
            {...iconProps}
            sx={getIconSx(status, config.color)}
          />
        </StatusTooltip>
      </Box>
    );
  }
);
